/**
 * Standard Goldmine vinyl grading scale, from best to worst condition.
 */
export type VinylGrade = "Mint" | "NM" | "VG+" | "VG" | "G+" | "G" | "Fair" | "Poor";

/**
 * All vinyl grades in order from best to worst condition.
 */
export const VINYL_GRADES: readonly VinylGrade[] = [
  "Mint",
  "NM",
  "VG+",
  "VG",
  "G+",
  "G",
  "Fair",
  "Poor",
] as const;

/**
 * List type discriminator.
 * - "collection": system list for owned vinyl (one per user)
 * - "wishlist": system list for desired vinyl (one per user)
 * - "custom": user-created lists
 */
export type ListType = "collection" | "wishlist" | "custom";

export type SortDirection = "asc" | "desc";

export type SortField =
  | "artistName"
  | "title"
  | "originalYear"
  | "artistCountry"
  | "format"
  | "addedAt"
  | "releaseType"
  | "recordGrade"
  | "date"
  | "offerPrice"
  | "offerDropPercent";

export interface SortRuleStep {
  field: SortField;
  direction: SortDirection;
  buckets?: string[];
}

export type SortRule = SortRuleStep[];

export interface ViewPreference {
  viewMode: "grid" | "browse";
  browseDimension?: "country" | "artist" | "year" | "genre";
}

/** Human-readable descriptions for each vinyl grade. */
export const GRADE_DESCRIPTIONS: Record<VinylGrade, string> = {
  Mint: "Perfect, unplayed condition",
  NM: "Nearly perfect, minimal signs of handling",
  "VG+": "Shows some signs of play, light surface noise",
  VG: "Consistent surface noise, light scratches",
  "G+": "Significant surface noise, plays through without skipping",
  G: "Heavy wear, plays through but with distortion",
  Fair: "Barely playable, significant damage",
  Poor: "Damaged, may skip or not play",
};

/** Stable persisted keys available for the custom-list icon picker. */
export const LIST_PRESET_ICONS = [
  "music_note",
  "headphones",
  "library_music",
  "queue_music",
  "piano",
  "mic",
  "radio",
  "graphic_eq",
  "equalizer",
  "audiotrack",
  "album",
  "star",
  "bookmark",
  "label",
  "sell",
  "local_offer",
  "storefront",
  "shopping_bag",
  "redeem",
  "card_giftcard",
  "celebration",
  "workspace_premium",
  "emoji_events",
] as const;
